import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Checkbox,
  Toolbar,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  AutoAwesome as WizardIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as ActivateIcon,
  Pause as DeactivateIcon,
  Update as UpdateIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { 
  getExams, 
  createExam, 
  updateExam, 
  deleteExam,
  bulkDeleteExams,
  bulkUpdateExamStatus,
  bulkUpdateExams,
  checkExamsWithGrades
} from '../services/examService';
import { getCourses } from '../services/courseService';
import { Exam, Course } from '../types';
import { useAuth } from '../context/AuthContext';
import SimpleExamWizard from '../components/SimpleExamWizard';
import api from '../api/api';

const Exams: React.FC = () => {
  const { user } = useAuth();
  const [exams, setExams] = useState<Exam[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  
  // Bulk selection state
  const [selectedExams, setSelectedExams] = useState<number[]>([]);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    title: '',
    description: '',
    exam_date: '',
    duration_minutes: '',
    total_marks: '',
    passing_marks: '',
    exam_type: '',
    is_active: null as boolean | null
  });
  
  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [examsWithGrades, setExamsWithGrades] = useState<number[]>([]);
  const [pendingDeleteExams, setPendingDeleteExams] = useState<number[]>([]);

  const classLevels = [
    'Pre-Primary 1', 'Pre-Primary 2',
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8',
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    class_level: '',
    exam_date: '',
    duration_minutes: '',
    total_marks: '',
    passing_marks: '',
    exam_type: 'midterm',
    is_active: true
  });

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importError, setImportError] = useState('');
  const [importAllClassLevels, setImportAllClassLevels] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsData, coursesData] = await Promise.all([
        getExams(undefined, user?.school_id),
        getCourses(user?.school_id)
      ]);
      setExams(examsData);
      setCourses(coursesData);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to load data';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!user?.school_id) {
      setError('School ID is required');
      setLoading(false);
      return;
    }

    try {
      if (editingExam) {
        await updateExam(editingExam.id, {
          ...formData,
          class_level: formData.class_level,
          duration_minutes: parseInt(formData.duration_minutes),
          total_marks: parseFloat(formData.total_marks),
          passing_marks: parseFloat(formData.passing_marks)
        });
      } else {
        await createExam({
          ...formData,
          class_level: formData.class_level,
          school_id: user.school_id,
          duration_minutes: parseInt(formData.duration_minutes),
          total_marks: parseFloat(formData.total_marks),
          passing_marks: parseFloat(formData.passing_marks)
        });
      }
      setDialogOpen(false);
      setEditingExam(null);
      resetForm();
      fetchData();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to save exam';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to save exam');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (exam: Exam) => {
    setEditingExam(exam);
    setFormData({
      title: exam.title,
      description: exam.description || '',
      class_level: exam.class_level,
      exam_date: exam.exam_date.split('T')[0],
      duration_minutes: exam.duration_minutes.toString(),
      total_marks: exam.total_marks.toString(),
      passing_marks: exam.passing_marks.toString(),
      exam_type: exam.exam_type,
      is_active: exam.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (examId: number) => {
    if (window.confirm('Are you sure you want to delete this exam?')) {
      try {
        await deleteExam(examId);
        fetchData();
      } catch (err: any) {
        const errorMessage = err?.response?.data?.detail || 
                           err?.response?.data?.message || 
                           err?.message || 
                           'Failed to delete exam';
        setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete exam');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      class_level: '',
      exam_date: '',
      duration_minutes: '',
      total_marks: '',
      passing_marks: '',
      exam_type: 'midterm',
      is_active: true
    });
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Bulk selection functions
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedExams(exams.map(exam => exam.id));
    } else {
      setSelectedExams([]);
    }
  };

  const handleSelectExam = (examId: number) => {
    setSelectedExams(prev => 
      prev.includes(examId) 
        ? prev.filter(id => id !== examId)
        : [...prev, examId]
    );
  };

  const handleBulkDelete = async () => {
    try {
      // First, check which exams have grades
      const examsWithGradesData = await checkExamsWithGrades(selectedExams);
      
      if (examsWithGradesData.length > 0) {
        // Some exams have grades, show warning dialog
        setExamsWithGrades(examsWithGradesData);
        setPendingDeleteExams(selectedExams);
        setWarningDialogOpen(true);
      } else {
        // No grades, proceed with normal confirmation
        if (window.confirm(`Are you sure you want to delete ${selectedExams.length} exams?`)) {
          await performBulkDelete(selectedExams);
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to check exams for grades';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to check exams for grades');
    }
  };

  const performBulkDelete = async (examIds: number[]) => {
    try {
      await bulkDeleteExams(examIds);
      setSelectedExams([]);
      setWarningDialogOpen(false);
      setExamsWithGrades([]);
      setPendingDeleteExams([]);
      fetchData();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to delete exams';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to delete exams');
    }
  };

  const handleBulkStatusUpdate = async (isActive: boolean) => {
    try {
      await bulkUpdateExamStatus(selectedExams, isActive);
      setSelectedExams([]);
      fetchData();
    } catch (err: any) {
      // Ensure we always set a string to error state
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to update exam status';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to update exam status');
    }
  };

  const handleBulkUpdate = async () => {
    try {
      const updateData: any = {};
      if (bulkUpdateData.title) updateData.title = bulkUpdateData.title;
      if (bulkUpdateData.description) updateData.description = bulkUpdateData.description;
      if (bulkUpdateData.exam_date) updateData.exam_date = new Date(bulkUpdateData.exam_date).toISOString();
      if (bulkUpdateData.duration_minutes) updateData.duration_minutes = parseInt(bulkUpdateData.duration_minutes);
      if (bulkUpdateData.total_marks) updateData.total_marks = parseFloat(bulkUpdateData.total_marks);
      if (bulkUpdateData.passing_marks) updateData.passing_marks = parseFloat(bulkUpdateData.passing_marks);
      if (bulkUpdateData.exam_type) updateData.exam_type = bulkUpdateData.exam_type;
      if (bulkUpdateData.is_active !== null) updateData.is_active = bulkUpdateData.is_active;

      await bulkUpdateExams(selectedExams, updateData);
      setSelectedExams([]);
      setBulkUpdateDialogOpen(false);
      setBulkUpdateData({
        title: '',
        description: '',
        exam_date: '',
        duration_minutes: '',
        total_marks: '',
        passing_marks: '',
        exam_type: '',
        is_active: null
      });
      fetchData();
    } catch (err: any) {
      // Ensure we always set a string to error state
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         err?.message || 
                         'Failed to update exams';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to update exams');
    }
  };

  const handleBulkStatusChange = (value: string) => {
    setBulkUpdateData(prev => ({ 
      ...prev, 
      is_active: value === '' ? null : (value === 'active' ? true : false)
    }));
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = `title,description,class_level,exam_date,duration_minutes,total_marks,passing_marks,exam_type,is_active\nSample Exam,Description,Grade 1,2024-07-01T09:00:00,90,100,40,midterm,true\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exam_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('all_class_levels', importAllClassLevels ? 'true' : 'false');
      const baseUrl = api.defaults.baseURL || process.env.REACT_APP_API_URL || window.location.origin;
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${baseUrl}/exams/bulk/import`, {
        method: 'POST',
        headers,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      fetchData();
    } catch (err: any) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Exams</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenImportDialog}
          >
            Bulk Import Exams
          </Button>
          <Button
            variant="outlined"
            startIcon={<WizardIcon />}
            onClick={() => setWizardOpen(true)}
          >
            Exam Wizard
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingExam(null);
              resetForm();
              setDialogOpen(true);
            }}
          >
            Add Exam
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Bulk Action Toolbar */}
      {selectedExams.length > 0 && (
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, bgcolor: 'primary.light', color: 'white', mb: 2 }}>
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
            {selectedExams.length} exam(s) selected
          </Typography>
          <Tooltip title="Bulk Actions">
            <IconButton
              color="inherit"
              onClick={(event) => setBulkActionAnchor(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={bulkActionAnchor}
            open={Boolean(bulkActionAnchor)}
            onClose={() => setBulkActionAnchor(null)}
          >
            <MenuItem onClick={() => { handleBulkDelete(); setBulkActionAnchor(null); }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Selected</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleBulkStatusUpdate(true); setBulkActionAnchor(null); }}>
              <ListItemIcon>
                <ActivateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Activate Selected</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleBulkStatusUpdate(false); setBulkActionAnchor(null); }}>
              <ListItemIcon>
                <DeactivateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Deactivate Selected</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setBulkUpdateDialogOpen(true); setBulkActionAnchor(null); }}>
              <ListItemIcon>
                <UpdateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update Selected</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedExams.length > 0 && selectedExams.length < exams.length}
                  checked={exams.length > 0 && selectedExams.length === exams.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Class Level</TableCell>
              <TableCell>Exam Date</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Total Marks</TableCell>
              <TableCell>Passing Marks</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exams.map((exam) => (
              <TableRow key={exam.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedExams.includes(exam.id)}
                    onChange={() => handleSelectExam(exam.id)}
                  />
                </TableCell>
                <TableCell>{exam.title}</TableCell>
                <TableCell>{exam.class_level}</TableCell>
                <TableCell>{new Date(exam.exam_date).toLocaleDateString()}</TableCell>
                <TableCell>{exam.duration_minutes} min</TableCell>
                <TableCell>{exam.total_marks}</TableCell>
                <TableCell>{exam.passing_marks}</TableCell>
                <TableCell>
                  <Chip label={exam.exam_type} size="small" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={exam.is_active ? 'Active' : 'Inactive'} 
                    color={exam.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(exam)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(exam.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingExam ? 'Edit Exam' : 'Add New Exam'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={handleChange('title')}
                required
              />
              
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
              />

              <FormControl fullWidth required>
                <InputLabel>Class Level</InputLabel>
                <Select
                  value={formData.class_level}
                  onChange={handleChange('class_level')}
                  label="Class Level"
                >
                  {classLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Exam Date"
                type="date"
                value={formData.exam_date}
                onChange={handleChange('exam_date')}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={handleChange('duration_minutes')}
                required
                inputProps={{ min: 1 }}
              />

              <TextField
                fullWidth
                label="Total Marks"
                type="number"
                value={formData.total_marks}
                onChange={handleChange('total_marks')}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />

              <TextField
                fullWidth
                label="Passing Marks"
                type="number"
                value={formData.passing_marks}
                onChange={handleChange('passing_marks')}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />

              <FormControl fullWidth required>
                <InputLabel>Exam Type</InputLabel>
                <Select
                  value={formData.exam_type}
                  onChange={handleChange('exam_type')}
                  label="Exam Type"
                >
                  <MenuItem value="midterm">Midterm</MenuItem>
                  <MenuItem value="final">Final</MenuItem>
                  <MenuItem value="quiz">Quiz</MenuItem>
                  <MenuItem value="assignment">Assignment</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : (editingExam ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialogOpen} onClose={() => setBulkUpdateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Update {selectedExams.length} Exam(s)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Leave fields empty to keep current values. Only filled fields will be updated.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={bulkUpdateData.title}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Leave empty to keep current"
            />
            
            <TextField
              fullWidth
              label="Description"
              value={bulkUpdateData.description}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              placeholder="Leave empty to keep current"
            />

            <TextField
              fullWidth
              label="Exam Date"
              type="date"
              value={bulkUpdateData.exam_date}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, exam_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={bulkUpdateData.duration_minutes}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, duration_minutes: e.target.value }))}
              inputProps={{ min: 1 }}
              placeholder="Leave empty to keep current"
            />

            <TextField
              fullWidth
              label="Total Marks"
              type="number"
              value={bulkUpdateData.total_marks}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, total_marks: e.target.value }))}
              inputProps={{ step: 0.01, min: 0 }}
              placeholder="Leave empty to keep current"
            />

            <TextField
              fullWidth
              label="Passing Marks"
              type="number"
              value={bulkUpdateData.passing_marks}
              onChange={(e) => setBulkUpdateData(prev => ({ ...prev, passing_marks: e.target.value }))}
              inputProps={{ step: 0.01, min: 0 }}
              placeholder="Leave empty to keep current"
            />

            <FormControl fullWidth>
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={bulkUpdateData.exam_type}
                onChange={(e) => setBulkUpdateData(prev => ({ ...prev, exam_type: e.target.value }))}
                label="Exam Type"
              >
                <MenuItem value="">Keep Current</MenuItem>
                <MenuItem value="midterm">Midterm</MenuItem>
                <MenuItem value="final">Final</MenuItem>
                <MenuItem value="quiz">Quiz</MenuItem>
                <MenuItem value="assignment">Assignment</MenuItem>
                <MenuItem value="project">Project</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={bulkUpdateData.is_active === null ? '' : (bulkUpdateData.is_active ? 'active' : 'inactive')}
                onChange={(e) => {
                  const value = e.target.value as string;
                  setBulkUpdateData(prev => ({ 
                    ...prev, 
                    is_active: value === '' ? null : (value === 'active' ? true : false)
                  }));
                }}
                label="Status"
              >
                <MenuItem value="">Keep Current</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkUpdate} variant="contained">
            Update {selectedExams.length} Exam(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog for Exams with Grades */}
      <Dialog open={warningDialogOpen} onClose={() => setWarningDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'warning.main' }}>
          ⚠️ Warning: Exams with Grades
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to delete {pendingDeleteExams.length} exam(s), and {examsWithGrades.length} of them have associated grades.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>This action will permanently delete:</strong>
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • {pendingDeleteExams.length} exam(s)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • All grades associated with {examsWithGrades.length} exam(s)
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All exam data and associated grades will be permanently lost.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => performBulkDelete(pendingDeleteExams)} 
            variant="contained" 
            color="error"
          >
            Delete All ({pendingDeleteExams.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Exams</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download CSV Template
            </Button>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.json"
              onChange={handleFileChange}
              style={{ marginTop: 16 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={importAllClassLevels}
                  onChange={e => setImportAllClassLevels(e.target.checked)}
                />
              }
              label="Create exams for all class levels (each row will be duplicated for every class level)"
            />
            {importing && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /> Importing...</Box>}
            {importError && <Alert severity="error">{importError}</Alert>}
            {importResult && (
              <Alert severity={importResult.success ? 'success' : 'warning'}>
                Imported: {importResult.imported} exam(s).<br />
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <strong>Errors:</strong>
                    <ul>
                      {importResult.errors.map((err: any, idx: number) => (
                        <li key={idx}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || importing}
            startIcon={<CloudUploadIcon />}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <SimpleExamWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={fetchData}
      />
    </Box>
  );
};

export default Exams; 